import { CanActivateFn, MaybeAsync, Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { inject } from "@angular/core";

/**
 * Kieltä käsittelevä guard:
 * - Jos URL:ssa on `locale`-parametri ja se on tuettu, sen arvo asetetaan sovellukseen ja tallennetaan localStorageen.
 * - Muussa tapauksessa tarkistetaan ensin localStorage, ja jos sieltäkään ei löydy validia kieltä, käytetään selaimen kieltä.
 * - Jos selaimen kieli ei ole tuettu, käytetään fallback-kielenä parametrina annettua kieltä (oletuksena "en", mikäli tuettu).
 *
 * @param route
 * @param state
 * @param fallbackLanguage Fallback-kieli, jota käytetään, jos muut vaihtoehdot eivät tuota validia kieltä.
 */
export const localeGuard: CanActivateFn = (route, state): MaybeAsync<boolean> => {
    const router = inject(Router);
    const translateService = inject(TranslateService);
    const supportedLanguages = translateService.getLangs();

    // Aseta fallback-kieli. Tämä voidaan myös injektoida konfiguraation kautta, mikäli halutaan.
    const fallbackLanguage = translateService.currentLang || translateService.defaultLang || 'en';

    // Tarkistetaan, onko URL:ssa locale-parametri, joka on tuettu
    const routeLocale = route?.queryParams?.['locale'];
    if (routeLocale && supportedLanguages.includes(routeLocale)) {
        translateService.use(routeLocale);
        localStorage.setItem('locale', routeLocale);
        return router.navigate(route.url, { queryParams: { ...route.queryParams, locale: undefined } });
    }

    // Tarkistetaan ensin tallennettu arvo localStoragesta
    const storedLocale = localStorage.getItem('locale');
    if (storedLocale && supportedLanguages.includes(storedLocale)) {
        translateService.use(storedLocale);
    } else {
        const browserLang = navigator.language.split('-')[0]; // Esim. "en-US" -> "en"
        // Jos selaimen kieli on tuettu, käytetään sitä. Muuten tarkistetaan fallback.
        const languageToUse = supportedLanguages.includes(browserLang)
            ? browserLang
            : (supportedLanguages.includes(fallbackLanguage) ? fallbackLanguage : supportedLanguages[0]);
        translateService.use(languageToUse);
        localStorage.setItem('locale', languageToUse);
    }

    return true;
};
